import { IAdjustmentReadResourceV22 } from 'components/basket/model/Basket';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { IUpdateOrderAdjustment, ILocalCouponAdjustment, OrderAdjustmentType } from '../model/Order';

export function useCouponCodes(
    initialCoupons: ILocalCouponAdjustment[] | undefined,
    adjustments?: IAdjustmentReadResourceV22[]
) {
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const [coupons, setCoupons] = React.useState<ILocalCouponAdjustment[]>(initialCoupons ?? []);
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const onCouponAdd = React.useCallback(
        (coupon: ILocalCouponAdjustment) => {
            if (coupons.some(item => item.couponCode === coupon.couponCode)) {
                return enqueueSnackbar(t('CHECKOUT_COUPON_DUPLICATE_ERROR'), { variant: 'warning' });
            }
            setCoupons(existingCoupons => [...existingCoupons, coupon]);
        },
        [coupons, enqueueSnackbar, t]
    );
    const onCouponRemove = React.useCallback((couponId: string) => {
        setCoupons(existingCoupons =>
            existingCoupons.filter(existingCoupon => existingCoupon.couponCode !== couponId)
        );
    }, []);
    const onCouponsReset = React.useCallback(() => {
        setCoupons([]);
    }, []);
    const couponAdjustments = React.useMemo(() => {
        const list: IAdjustmentReadResourceV22[] = [];
        if (Array.isArray(coupons) && adjustments) {
            coupons.forEach(coupon => {
                const adjustment = adjustments?.find(
                    adjustment => adjustment.couponCode === coupon.couponCode
                );
                if (adjustment) {
                    list.push(adjustment);
                }
            });
        }
        return list;
    }, [coupons, adjustments]);
    const showCoupons = React.useMemo(() => !!settings?.couponProvider, [settings?.couponProvider]);
    const couponsForCheckout = React.useMemo(() => {
        const adjustments: Partial<IUpdateOrderAdjustment>[] = [];
        if (coupons.length > 0) {
            coupons.forEach(coupon => {
                adjustments.push({
                    type: OrderAdjustmentType.DISCOUNT,
                    couponCode: coupon.couponCode,
                    selectedOn: coupon.selectedOn
                });
            });
        }
        return adjustments;
    }, [coupons]);
    return {
        coupons,
        couponAdjustments,
        onCouponAdd,
        onCouponRemove,
        onCouponsReset,
        showCoupons,
        couponsForCheckout
    };
}
